import React from 'react';

import { Progress } from 'antd-v5';
import { DateTime } from 'luxon';
import { MdInsertDriveFile } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { Button, Text } from 'scouting-ui/lib';

import type { UploadedImport } from '../../types';
import styles from './ImportStatusList.module.scss';

interface ImportStatusListProps {
  uploading: boolean;
  uploadedFiles: UploadedImport[];
  uploadError: boolean;
  selectedFile: File | null;
  errorsCount: number;
  onCancel: () => void;
  onRetry: (file: File) => void;
  onShowErrors: () => void;
}

/** Presentational status list mirroring the legacy UploadsList/UploadItem. */
const ImportStatusList: React.FC<ImportStatusListProps> = props => {
  const {
    uploading,
    uploadedFiles,
    uploadError,
    selectedFile,
    errorsCount,
    onCancel,
    onRetry,
    onShowErrors,
  } = props;

  return (
    <div className={styles.container}>
      <Text font="body-body-2">
        <span className={styles.heading}>
          <FormattedMessage id="advancementImport.files" />:
        </span>
      </Text>

      {uploadedFiles.map(file => {
        const parsed = DateTime.fromISO(file.uploadedDate);
        return (
          <div className={styles.item} key={`${file.name}${file.uploadedDate}`}>
            <MdInsertDriveFile className={styles.fileIcon} />
            <div className={styles.body}>
              <div className={styles.titleRow}>
                <Text font="body-caption">{file.name}</Text>
              </div>
              <Progress
                percent={100}
                status="success"
                showInfo={false}
                size="small"
              />
              <Text font="body-overline">
                <span className={styles.muted}>
                  <FormattedMessage id="advancementImport.processing" />
                  <br />
                  <FormattedMessage
                    id="advancementImport.uploadedDate"
                    values={{
                      date: parsed.toFormat('MMMM d, yyyy'),
                      time: parsed.toFormat('h:mma').toLowerCase(),
                    }}
                  />
                </span>
              </Text>
            </div>
          </div>
        );
      })}

      {uploading && (
        <div className={styles.item}>
          <MdInsertDriveFile className={styles.fileIcon} />
          <div className={styles.body}>
            <div className={styles.titleRow}>
              <Text font="body-caption">
                <FormattedMessage id="advancementImport.uploading" />
              </Text>
              <Button
                type="link"
                size="small"
                danger
                className={styles.cancel}
                onClick={onCancel}
              >
                <FormattedMessage id="advancementImport.cancel" />
              </Button>
            </div>
            <Progress
              percent={100}
              status="active"
              showInfo={false}
              size="small"
            />
          </div>
        </div>
      )}

      {uploadError && selectedFile && (
        <div className={styles.item}>
          <MdInsertDriveFile className={styles.fileIcon} />
          <div className={styles.body}>
            <div className={styles.titleRow}>
              <Text font="body-caption">
                <FormattedMessage id="advancementImport.error" />
              </Text>
              <Button
                type="link"
                size="small"
                className={styles.link}
                onClick={() => onRetry(selectedFile)}
              >
                <FormattedMessage id="advancementImport.retry" />
              </Button>
            </div>
            <Progress
              percent={100}
              status="exception"
              showInfo={false}
              size="small"
            />
            <div className={styles.errorRow}>
              <Button
                type="link"
                size="small"
                className={styles.link}
                onClick={onShowErrors}
              >
                <FormattedMessage id="advancementImport.viewErrors" />
              </Button>
              <Text font="body-overline">
                <span className={styles.muted}>
                  <FormattedMessage
                    id="advancementImport.errorCount"
                    values={{ count: errorsCount }}
                  />
                </span>
              </Text>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportStatusList;
